import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SectionCard, InfoNote } from "@/components/common/Cards";
import { Field, MoneyInput, NumInput } from "@/components/common/Inputs";
import { Sel } from "@/components/common/Sel";
import { OPEX_CATEGORIES, newOpex } from "@/lib/engines/isp";
import { fmtRp } from "@/lib/format";

export default function OpexPanel({ data, setData, readOnly }) {
  const setOpex = (updater) => setData((d) => ({ ...d, opex: typeof updater === "function" ? updater(d.opex) : updater }));
  const setBw = (patch) => setData((d) => ({ ...d, bandwidth: { ...d.bandwidth, ...patch } }));
  const setSub = (patch) => setData((d) => ({ ...d, subscriber: { ...d.subscriber, ...patch } }));
  const setFees = (patch) => setData((d) => ({ ...d, fees: { ...d.fees, ...patch } }));

  const add = () => setOpex((o) => [...o, newOpex()]);
  const remove = (id) => setOpex((o) => o.filter((x) => x.id !== id));
  const update = (id, patch) => setOpex((o) => o.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const totalOpex = data.opex.reduce((a, c) => a + (c.monthly || 0), 0);

  return (
    <div className="space-y-5">
      <SectionCard
        title="OPEX bulanan"
        eyebrow="Biaya operasional tetap"
        description="Biaya operasional yang muncul setiap bulan (SDM, sewa, listrik, dll)."
        actions={
          <Button onClick={add} disabled={readOnly} size="sm" data-testid="isp-opex-add">
            <Plus className="h-4 w-4" /> Tambah item
          </Button>
        }
        bodyClassName="p-0"
        testid="isp-opex-card"
      >
        <div className="overflow-x-auto scroll-gold">
          <table className="dense-table w-full text-xs" data-testid="isp-opex-table">
            <thead>
              <tr className="bg-secondary">
                <th className="text-left px-4 py-3 w-[180px]">Kategori</th>
                <th className="text-left px-3 py-3">Nama item</th>
                <th className="text-right px-3 py-3 w-[180px]">Biaya per bulan</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {data.opex.map((c) => (
                <tr key={c.id} className="border-t border-border hover:bg-[hsl(220_10%_9%)]" data-testid={`isp-opex-row-${c.id}`}>
                  <td className="px-4 py-2">
                    <Sel value={c.category} onChange={(v) => update(c.id, { category: v })} options={OPEX_CATEGORIES.map((x) => ({ value: x, label: x }))} testid={`isp-opex-category-${c.id}`} disabled={readOnly} />
                  </td>
                  <td className="px-3 py-2">
                    <Input value={c.name} onChange={(e) => update(c.id, { name: e.target.value })} className="h-9 bg-[hsl(220_12%_8%)]" disabled={readOnly} data-testid={`isp-opex-name-${c.id}`} />
                  </td>
                  <td className="px-3 py-2">
                    <MoneyInput value={c.monthly} onChange={(v) => update(c.id, { monthly: v })} disabled={readOnly} testid={`isp-opex-monthly-${c.id}`} />
                  </td>
                  <td className="px-3 py-2">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-[hsl(0_72%_62%)]" onClick={() => remove(c.id)} disabled={readOnly} data-testid={`isp-opex-remove-${c.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
              {data.opex.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-muted-foreground">Belum ada item OPEX.</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="bg-secondary border-t border-border">
                <td colSpan={2} className="px-4 py-3 font-semibold">Total OPEX/bulan</td>
                <td className="px-3 py-3 text-right num font-semibold text-primary" data-testid="isp-opex-total">{fmtRp(totalOpex)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </SectionCard>

      <div className="grid lg:grid-cols-3 gap-5">
        <SectionCard title="Bandwidth upstream" eyebrow="IP transit / Peering" testid="isp-bandwidth-card">
          <div className="space-y-3">
            <Field label="Harga per Mbps / bulan" hint="Harga rata-rata ke upstream (IIX/IX/IP transit).">
              <MoneyInput value={data.bandwidth.pricePerMbps} onChange={(v) => setBw({ pricePerMbps: v })} testid="isp-bw-price" disabled={readOnly} />
            </Field>
            <Field label="Minimum komitmen" hint="Beberapa upstream mewajibkan kapasitas minimum.">
              <NumInput value={data.bandwidth.minCommitMbps} onChange={(v) => setBw({ minCommitMbps: v })} suffix="Mbps" decimals={0} testid="isp-bw-min" disabled={readOnly} />
            </Field>
          </div>
        </SectionCard>

        <SectionCard title="CPE & instalasi" eyebrow="Per pelanggan" testid="isp-sub-card">
          <div className="space-y-3">
            <Field label="Harga CPE (ONT + router)">
              <MoneyInput value={data.subscriber.cpeCost} onChange={(v) => setSub({ cpeCost: v })} testid="isp-sub-cpe" disabled={readOnly} />
            </Field>
            <Field label="Biaya instalasi / drop core">
              <MoneyInput value={data.subscriber.installCost} onChange={(v) => setSub({ installCost: v })} testid="isp-sub-install" disabled={readOnly} />
            </Field>
            <Field label="Biaya aktivasi dibebankan ke pelanggan">
              <MoneyInput value={data.subscriber.installFee} onChange={(v) => setSub({ installFee: v })} testid="isp-sub-fee" disabled={readOnly} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Amortisasi CPE">
                <NumInput value={data.subscriber.amortMonths} onChange={(v) => setSub({ amortMonths: v })} suffix="bln" decimals={0} testid="isp-sub-amort" disabled={readOnly} />
              </Field>
              <Field label="Biaya variabel/pelanggan" hint="Billing, support ticket, dll.">
                <MoneyInput value={data.subscriber.variablePerSub} onChange={(v) => setSub({ variablePerSub: v })} testid="isp-sub-variable" disabled={readOnly} />
              </Field>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="BHP, USO & fee" eyebrow="Regulator" testid="isp-fees-card">
          <div className="space-y-3">
            <Field label="BHP Telekomunikasi" hint="Biaya Hak Penyelenggaraan (umumnya 0.5% dari pendapatan kotor).">
              <NumInput value={data.fees.bhpPct} onChange={(v) => setFees({ bhpPct: v })} suffix="%" decimals={2} testid="isp-fees-bhp" disabled={readOnly} />
            </Field>
            <Field label="USO" hint="Universal Service Obligation (umumnya 1.25%).">
              <NumInput value={data.fees.usoPct} onChange={(v) => setFees({ usoPct: v })} suffix="%" decimals={2} testid="isp-fees-uso" disabled={readOnly} />
            </Field>
            <Field label="Fee lain" hint="Komisi distributor, fee payment gateway, dll.">
              <NumInput value={data.fees.otherPct} onChange={(v) => setFees({ otherPct: v })} suffix="%" decimals={2} testid="isp-fees-other" disabled={readOnly} />
            </Field>
            <InfoNote>Fee dikenakan terhadap pendapatan kotor paket tertentu.</InfoNote>
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Alokasi biaya tetap ke paket" testid="isp-allocation-card">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs text-muted-foreground">Metode alokasi</span>
          <div className="inline-flex rounded-lg border border-border bg-[hsl(220_12%_8%)] p-1">
            {[{ value: "by_speed", label: "Proporsional kecepatan" }, { value: "per_subscriber", label: "Rata per pelanggan" }].map((o) => (
              <button key={o.value} onClick={() => setData((d) => ({ ...d, allocation: o.value }))} className={`press rounded-md px-2.5 py-1 text-xs ${data.allocation === o.value ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid={`isp-allocation-${o.value}`} disabled={readOnly}>
                {o.label}
              </button>
            ))}
          </div>
          <span className="text-xs text-muted-foreground">· Biaya CAPEX + OPEX didistribusikan ke paket sesuai metode ini.</span>
        </div>
      </SectionCard>
    </div>
  );
}
